/**
 * @lodex-pro/web-shared/api/query-client — TanStack Query client singleton + provider.
 *
 * Phase 9 / Plan 09-03 — shared QueryClient configuration for all lodeX Pro web apps.
 *
 * The 401-refresh-retry is handled by `authedFetch` in `api-client.ts` (Plan 04-11
 * CR-06 fix). This QueryClient intentionally does NOT add its own 401/retry logic —
 * doing so would conflict with the existing refresh-retry dance in authedFetch.
 *
 * All consuming apps (web-dispatch, web-billing, web-portal, web-admin) mount a
 * single `<QueryProvider>` at their root. An optional `client` prop allows test suites
 * to inject a fresh QueryClient without mutating the module-level singleton.
 *
 * OPEN-182 — the provider empties its cache when Amplify signs the user out (the `auth` Hub's
 * `signedOut`, which every sign-out path raises: an app's Sign out button, `useAuth().signOutAll`,
 * the tenant picker's switch). Without it the singleton outlived the session: the next user to
 * sign in in the same tab was shown the last user's still-fresh cached data (another company's
 * name, tickets, settings) until each query went stale. The API never served it; the cache did.
 *
 * Spec: CONTEXT D-06 (web foundation); RESEARCH Pattern 1 (no-double-401 constraint).
 */

import * as React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Hub } from 'aws-amplify/utils';

/**
 * Create a new QueryClient with lodeX Pro defaults.
 *
 * - `retry: 1` — one automatic retry on network failure (authedFetch handles auth retries).
 * - `refetchOnWindowFocus: false` — dispatchers work in a single focused window; noisy refetches
 *   distract from the live polling cadence controlled by each feature hook.
 * - `staleTime: 25_000` — 25-second stale threshold pairs with the 30-second poll interval used
 *   by position/queue hooks. Data stays fresh between polls without triggering redundant fetches.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
        staleTime: 25_000,
      },
    },
  });
}

/** Module-level singleton — shared across all QueryProvider mounts that omit the `client` prop. */
const defaultClient = createQueryClient();

export interface QueryProviderProps {
  /** React subtree that gets access to the query cache. */
  readonly children: React.ReactNode;
  /**
   * Optional client override. Inject a fresh `createQueryClient()` in tests to
   * isolate the cache between test cases.
   */
  readonly client?: QueryClient;
}

/**
 * Mounts TanStack Query's `QueryClientProvider`. Consumer apps place this at the
 * root of their React tree (above any feature hook that calls `useApiQuery`).
 */
export function QueryProvider({ children, client }: QueryProviderProps): React.JSX.Element {
  const active = client ?? defaultClient;
  React.useEffect(() => clearOnSignOut(active), [active]);
  return React.createElement(QueryClientProvider, { client: active }, children);
}

/**
 * Empty `client` whenever Amplify reports `signedOut` (OPEN-182). Amplify raises it after the
 * tokens are cleared, so nothing refetched afterwards can be the old user's. Returns the
 * unsubscribe. Other auth events (sign-in, token refresh, a failed refresh) keep the cache.
 */
export function clearOnSignOut(client: QueryClient): () => void {
  return Hub.listen('auth', ({ payload }) => {
    if (payload.event === 'signedOut') client.clear();
  });
}
